import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getBranches, createExpense } from '../utilities/api';
import '../masters/CategoryMaster.css';

const AddExpense = () => {
    const toast = useRef(null);
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [saving, setSaving] = useState(false);
    const [branchOptions, setBranchOptions] = useState([]);
    const [branchId, setBranchId] = useState(null);
    const [expenseName, setExpenseName] = useState('');

    useEffect(() => {
        const load = async () => {
            setLoadingMeta(true);
            try {
                const clientId = localStorage.getItem('clientid');
                const branches = await getBranches(clientId);
                const list = Array.isArray(branches) ? branches : [];
                setBranchOptions(list.map((b) => ({
                    label: b.branch_name || b.branchname || b.name || String(b.branch_id),
                    value: String(b.branch_id)
                })));
            } catch {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load branches' });
            } finally {
                setLoadingMeta(false);
            }
        };
        load();
    }, []);

    const canSubmit = useMemo(() => branchId && expenseName.trim(), [branchId, expenseName]);

    const handleReset = () => {
        setBranchId(null);
        setExpenseName('');
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please enter expense name and select branch' });
            return;
        }
        setSaving(true);
        try {
            const res = await createExpense({ expanses_name: expenseName.trim(), branch_id: branchId });
            const ok = res?.status === true || res?.status === 'success' || res?.success === true;
            if (ok) {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: res?.message || 'Expense created successfully' });
                handleReset();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res?.message || res?.error || 'Failed to create expense' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err.message || 'Request failed' });
        } finally {
            setSaving(false);
        }
    };

    if (loadingMeta) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="masters-page-outer">
            <Toast ref={toast} />

            {/* Page header */}
            <div className="masters-page-header">
                <h2>Add Expense</h2>
                <p>Create a new expense type for a branch</p>
            </div>

            {/* Form card */}
            <Card className="bill-reports-container">
                <div className="p-fluid formgrid grid">

                    <div className="field col-12 md:col-6">
                        <label htmlFor="expanses_name" className="font-medium">Expense Name</label>
                        <InputText
                            id="expanses_name"
                            value={expenseName}
                            onChange={(e) => setExpenseName(e.target.value)}
                            placeholder="Enter expense name"
                        />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="branch_id" className="font-medium">Branch</label>
                        <Dropdown
                            id="branch_id"
                            value={branchId}
                            options={branchOptions}
                            onChange={(e) => setBranchId(e.value)}
                            placeholder="Select branch"
                            filter
                            showClear
                        />
                    </div>

                    <div className="field col-12">
                        <hr style={{ borderColor: '#e5d8f5', margin: '0.5rem 0 1rem' }} />
                        <div className="flex gap-2">
                            <Button label="Submit" icon="pi pi-check" className="sub-dialog-save" onClick={handleSubmit} loading={saving} disabled={!canSubmit} />
                            <Button label="Clear" icon="pi pi-refresh" className="sub-dialog-cancel" onClick={handleReset} disabled={saving} />
                        </div>
                    </div>

                </div>
            </Card>
        </div>
    );
};

export default AddExpense;
